(function () {
  'use strict';
  const VERSION = '2026-09-30.4';
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  function safeURL(value, base) {
    try {
      const url = new URL(value, base || 'https://lunere.com');
      if (!/^https?:$/.test(url.protocol)) return '';
      if (/^\/(account|checkouts?|orders)(\/|$)/.test(url.pathname)) return url.origin + '/' + url.pathname.split('/')[1];
      return url.origin + url.pathname;
    } catch (_) { return ''; }
  }
  function watchDelta(previousTime, currentTime, elapsed, rate, eligible) {
    const progress = currentTime - previousTime;
    if (!eligible || elapsed <= 0 || elapsed > 2 || progress <= 0 || progress > elapsed * rate + 0.75) return 0;
    return Math.min(elapsed, progress / Math.max(0.1, rate));
  }
  function confirmedCartProperties(detail) {
    const meta=detail?.data, item=detail?.resource;
    if(!meta || meta.didError || meta.source!=='product-form-component' || !item?.variant_id || !item?.product_id) return null;
    const quantity=Number(meta.itemCount || 0);
    if(quantity<=0 || !Number.isFinite(quantity))return null;
    return {product_id:String(item.product_id),variant_id:String(item.variant_id),product_title:String(item.product_title || item.title || '').slice(0,160),quantity,value:Number(item.final_price ?? item.price ?? 0)*quantity/100,currency:'USD',tracking_source:'confirmed_storefront_cart'};
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { safeURL, watchDelta, confirmedCartProperties };
  if (typeof window === 'undefined' || window.__lunerePostHog) return;
  window.__lunerePostHog = true;
  const configNode = document.getElementById('lunere-posthog-config');
  if (!configNode || !/^(www\.)?lunere\.com$/.test(location.hostname)) return;
  const config = JSON.parse(configNode.textContent);
  const params = new URLSearchParams(location.search);
  const readStored = (key) => { try { return sessionStorage.getItem(key); } catch (_) { return null; } };
  const store = (key, value) => { try { sessionStorage.setItem(key, value); } catch (_) {} };
  const testMode = params.get('lunere_test') === '1' || readStored('lunere_analytics_test') === '1';
  if (testMode) store('lunere_analytics_test', '1');
  const runId = (params.get('lunere_test_run') || readStored('lunere_analytics_test_run') || '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0,80);
  if (testMode && runId) store('lunere_analytics_test_run', runId);
  const pageId = crypto.randomUUID();
  const bridgeName = 'lunere_ph_bridge';
  let ph, enabled = false, loading = false, started = false, tickAt = performance.now();
  let visibleSeconds = 0, activeSeconds = 0, lastInteraction = performance.now(), maxScroll = 0;
  let attribution = {};
  const videos = new Map(), sections = new Map(), scrollSent = new Set();
  const sectionName = (node) => {
    const section = node && node.closest('section');
    return section ? (section.id || (section.classList.contains('hero') ? 'hero' : section.classList[0] || 'section')) : 'navigation';
  };
  const allowed = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  const base = () => ({site_name:'Lunere',site_domain:'lunere.com',tracking_version:VERSION,page_instance_id:pageId,page_type:config.pageType,is_test:testMode,test_run:runId || undefined});
  function capture(name, properties, options) {
    if (!enabled || !ph || !allowed()) return;
    ph.capture(name, Object.assign(base(), properties || {}), options);
  }
  function writeBridge() {
    if (!enabled || !ph || !allowed()) return;
    const value = {id:ph.get_distinct_id(),session:ph.get_session_id(),at:Date.now(),utm:attribution,is_test:testMode,test_run:runId};
    document.cookie = bridgeName + '=' + encodeURIComponent(JSON.stringify(value)) + '; Path=/; Max-Age=1800; SameSite=Lax; Secure';
  }
  function prepareAttribution() {
    try { attribution = JSON.parse(readStored('lunere_utm') || '{}'); } catch (_) { attribution = {}; }
    UTM_KEYS.forEach(key => {
      const value = params.get(key);
      if (value) attribution[key] = value.replace(/[^a-zA-Z0-9 _|.\-]/g, '').slice(0,150);
    });
    store('lunere_utm', JSON.stringify(attribution));
    ph.register(Object.assign(base(), attribution));
  }
  function sanitize(event) {
    if (!enabled || !allowed()) return null;
    const p = event.properties || {};
    ['$current_url','$referrer','$initial_current_url','$initial_referrer','$session_entry_url'].forEach(key => { if (p[key]) p[key] = safeURL(p[key]); });
    ['$set','$set_once'].forEach(key => {
      if (!p[key]) return;
      Object.keys(p[key]).forEach(name => { if (/url|referrer/.test(name)) p[key][name] = safeURL(p[key][name]); });
    });
    ['email','phone','name','address','$ip','fbclid','gclid','msclkid'].forEach(key => delete p[key]);
    Object.assign(p, base());
    return event;
  }
  function videoProperties(state) {
    const v = state.video;
    return {video_id:state.id,video_type:state.type,video_duration_seconds:Number.isFinite(v.duration)?Math.round(v.duration*10)/10:0,playhead_seconds:Math.round(v.currentTime*10)/10,max_playhead_seconds:Math.round(state.maxPosition*10)/10,watch_seconds:Math.round(state.watch*10)/10,sound_on_watch_seconds:Math.round(state.sound*10)/10,muted:v.muted || v.volume === 0,playback_rate:v.playbackRate};
  }
  function summary(reason) {
    capture('lunere_page_summary',{reason,visible_seconds:Math.round(visibleSeconds),active_seconds:Math.round(activeSeconds),max_scroll_percent:maxScroll});
    videos.forEach(s => { if(s.watch > 0) capture('lunere_video_summary',Object.assign(videoProperties(s),{reason})); });
    sections.forEach((s,id) => { if(s.seconds>0) capture('lunere_section_summary',{section_id:id,visible_seconds:Math.round(s.seconds),reason}); });
    writeBridge();
  }
  function bindVideo(video) {
    if (videos.has(video)) return;
    const card = video.closest('.review-card');
    const identity = card?.querySelector('.review-name')?.textContent.trim() || video.getAttribute('aria-label') || 'video';
    const state = {video,type:video.id==='lunere-vsl'?'vsl':'testimonial',id:video.id==='lunere-vsl'?'lunere_vsl_v3':identity.slice(0,80),watch:0,sound:0,lastTime:video.currentTime,maxPosition:0,visible:false,started:false,soundStarted:false,milestones:new Set(),percentMilestones:new Set()};
    videos.set(video,state);
    videoObserver.observe(video);
    ['play','pause','ended','volumechange','ratechange','seeking','seeked','error'].forEach(name => video.addEventListener(name,()=>{
      if(name==='play' && !state.started){state.started=true;capture('lunere_video_start',videoProperties(state));}
      if(!video.muted && video.volume>0 && !video.paused && !state.soundStarted){state.soundStarted=true;capture('lunere_video_sound_on',videoProperties(state));}
      if(name==='pause' || name==='ended') {capture(name==='ended'?'lunere_video_complete':'lunere_video_pause',videoProperties(state));}
      if(name==='ratechange')capture('lunere_video_speed_change',videoProperties(state));
      if(name==='error')capture('lunere_video_error',{video_id:state.id,error_code:video.error?.code || 0});
      state.lastTime=video.currentTime;
    }));
    if(!video.paused){state.started=true;capture('lunere_video_start',videoProperties(state));}
  }
  const videoObserver = new IntersectionObserver(entries=>entries.forEach(entry=>{const s=videos.get(entry.target);if(s)s.visible=entry.isIntersecting && entry.intersectionRatio>=0.25;}),{threshold:[0,0.25]});
  const sectionObserver = new IntersectionObserver(entries=>entries.forEach(entry=>{
    const id=sectionName(entry.target),s=sections.get(id);
    if(!s)return;
    s.visible=entry.isIntersecting;
    if(s.visible && !s.seen){s.seen=true;capture('lunere_section_view',{section_id:id});}
  }),{rootMargin:'-15% 0px -15% 0px',threshold:0});
  function scrollDepth() {
    const height=document.documentElement.scrollHeight-innerHeight;
    const percent=height<=0?100:Math.min(100,Math.round(scrollY/height*100));
    maxScroll=Math.max(maxScroll,percent);
    [25,50,75,90,100].forEach(mark=>{if(percent>=mark&&!scrollSent.has(mark)){scrollSent.add(mark);capture('lunere_scroll_depth',{percent:mark});}});
  }
  function bindPage() {
    document.querySelectorAll('video').forEach(bindVideo);
    document.querySelectorAll('#lunere-landing section').forEach(node=>{const id=sectionName(node);sections.set(id,{seconds:0,visible:false,seen:false});sectionObserver.observe(node);});
    new MutationObserver(()=>document.querySelectorAll('video').forEach(bindVideo)).observe(document.body,{childList:true,subtree:true});
    document.addEventListener('click',event=>{
      lastInteraction=performance.now();
      const el=event.target.closest('a,button,summary,[role="button"]');
      if(!el || el.closest('form[action*="/account"],.shopify-cleanslate'))return;
      const label=(el.getAttribute('aria-label')||el.textContent||'').replace(/\s+/g,' ').trim().slice(0,160);
      const href=el.getAttribute('href')||'';
      const props={label,section_id:sectionName(el),element_id:el.id||undefined,destination:href.startsWith('#')?href:safeURL(href)};
      capture('lunere_element_click',props);
      if(el.matches('.button,.text-link,[data-lunere-atc],button[type="submit"]') || /\/products\//.test(href))capture('lunere_cta_click',props);
      if(el.matches('.vsl-start,.watch-first'))capture('lunere_vsl_unmute_click',{control:el.matches('.watch-first')?'watch_first':'video_overlay'});
      if(el.matches('.review-play'))capture('lunere_testimonial_click',{video_id:el.dataset.slug,reviewer:el.dataset.name});
      if(el.matches('summary'))capture('lunere_faq_toggle',{question:label,action:el.parentElement.open?'close':'open'});
      if(el.matches('[name="checkout"]')||/\/checkout(?:[/?]|$)/.test(href)){writeBridge();capture('lunere_checkout_intent',props);}
    },true);
    document.addEventListener('submit',event=>{if(/\/cart\/add/.test(event.target.action||''))capture('lunere_add_to_cart_intent',{section_id:sectionName(event.target)});writeBridge();},true);
    ['pointerdown','keydown','touchstart','scroll'].forEach(name=>window.addEventListener(name,()=>{lastInteraction=performance.now();},{passive:true}));
    window.addEventListener('scroll',scrollDepth,{passive:true});
    document.addEventListener('visibilitychange',()=>{if(document.hidden)summary('hidden');tickAt=performance.now();videos.forEach(s=>s.lastTime=s.video.currentTime);});
    window.addEventListener('pagehide',()=>{summary('pagehide');capture('$pageleave');});
    setInterval(()=>{
      const now=performance.now(),dt=(now-tickAt)/1000;tickAt=now;
      if(!enabled||!allowed()||document.hidden||dt<=0||dt>2){videos.forEach(s=>s.lastTime=s.video.currentTime);return;}
      visibleSeconds+=dt;
      let viewingVideo=false;
      videos.forEach(s=>{
        const v=s.video,eligible=s.visible&&!v.paused&&!v.seeking&&v.readyState>=3;
        const delta=watchDelta(s.lastTime,v.currentTime,dt,v.playbackRate,eligible);s.lastTime=v.currentTime;
        s.watch+=delta;if(!v.muted&&v.volume>0)s.sound+=delta;
        if(delta>0){s.maxPosition=Math.max(s.maxPosition,v.currentTime);if(!v.muted&&v.volume>0)viewingVideo=true;}
        if(!v.muted&&v.volume>0&&!v.paused&&!s.soundStarted){s.soundStarted=true;capture('lunere_video_sound_on',videoProperties(s));}
        [15,30,60,120,180,240,300].forEach(mark=>{if(s.sound>=mark&&!s.milestones.has(mark)){s.milestones.add(mark);capture('lunere_video_watch_milestone',Object.assign(videoProperties(s),{milestone_seconds:mark}));}});
        if(Number.isFinite(v.duration)&&v.duration>0)[25,50,75,90].forEach(mark=>{if(s.sound>=v.duration*mark/100&&!s.percentMilestones.has(mark)){s.percentMilestones.add(mark);capture('lunere_video_watch_percent',Object.assign(videoProperties(s),{percent:mark}));}});
      });
      if(now-lastInteraction<30000 || viewingVideo)activeSeconds+=dt;
      sections.forEach(s=>{if(s.visible)s.seconds+=dt;});
    },500);
    setInterval(()=>{if(enabled&&!document.hidden)summary('heartbeat');},15000);
    scrollDepth();
  }
  function ready(instance) {
    ph=instance;loading=false;
    if(!allowed()){enabled=false;ph.opt_out_capturing();return;}
    enabled=true;prepareAttribution();writeBridge();
    capture('$pageview',{$current_url:safeURL(location.href),$referrer:safeURL(document.referrer)});
    capture('lunere_tracking_ready');
    if(config.productId)capture('lunere_product_view',{product_id:String(config.productId),product_title:config.productTitle});
    if(!started){started=true;bindPage();}
  }
  function start() {
    if(!allowed()){
      enabled=false;
      if(ph){ph.stopSessionRecording();ph.opt_out_capturing();}
      document.cookie=bridgeName+'=; Path=/; Max-Age=0; SameSite=Lax; Secure';
      return;
    }
    if(enabled||loading)return;
    if(ph){enabled=true;ph.opt_in_capturing({captureEventName:false});ph.startSessionRecording();ready(ph);return;}
    loading=true;enabled=true;
    const options={api_host:config.apiHost,ui_host:'https://us.posthog.com',defaults:'2026-01-30',capture_pageview:false,capture_pageleave:false,person_profiles:'never',persistence:'localStorage+cookie',autocapture:{mask_all_text:true,mask_all_element_attributes:true},capture_performance:false,disable_session_recording:false,enable_recording_console_log:false,session_recording:{maskAllInputs:true,maskTextSelector:'input,textarea,[contenteditable="true"],.customer,form[action*="/account"]',blockSelector:'iframe,form[action*="/account"],.shopify-payment-button',recordCrossOriginIframes:false,recordCanvas:false,recordHeaders:false,recordBody:false,streamNetworkBody:false,captureJsonLd:false},before_send:sanitize,loaded:ready};
    if(window.posthog?.init){window.posthog.init(config.token,options);return;}
    window.posthog=[];window.posthog._i=[[config.token,options]];window.posthog.__SV=1.2;
    const script=document.createElement('script');script.async=true;script.src='https://us-assets.i.posthog.com/static/array.js';script.onerror=()=>{loading=false;enabled=false;};document.head.appendChild(script);
  }
  document.addEventListener('visitorConsentCollected',start);
  if(window.Shopify?.customerPrivacy)start();
  else if(window.Shopify?.loadFeatures)window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}],error=>{if(!error)start();});
})();
